/**
 * Spanish labels for Meta's campaign objectives and adset optimization goals.
 * Lifted verbatim from the old MetaCampaignInsights page so the control panel keeps
 * the same vocabulary users are used to.
 */

const campaignObjectiveLabels: Record<string, string> = {
  OUTCOME_AWARENESS: 'Reconocimiento',
  OUTCOME_ENGAGEMENT: 'Interacción',
  OUTCOME_LEADS: 'Clientes potenciales',
  OUTCOME_SALES: 'Ventas',
  OUTCOME_TRAFFIC: 'Tráfico',
  OUTCOME_APP_PROMOTION: 'Promoción de app',
  BRAND_AWARENESS: 'Reconocimiento de marca',
  REACH: 'Alcance',
  LINK_CLICKS: 'Clics en enlace',
  POST_ENGAGEMENT: 'Interacción con publicación',
  PAGE_LIKES: 'Me gusta de página',
  EVENT_RESPONSES: 'Respuestas a evento',
  VIDEO_VIEWS: 'Reproducciones de video',
  LEAD_GENERATION: 'Generación de leads',
  MESSAGES: 'Mensajes',
  CONVERSIONS: 'Conversiones',
  PRODUCT_CATALOG_SALES: 'Ventas del catálogo',
  STORE_VISITS: 'Visitas a tienda',
  APP_INSTALLS: 'Instalaciones de app',
};

const adSetOptimizationGoalLabels: Record<string, string> = {
  NONE: 'Ninguno',
  APP_INSTALLS: 'Instalaciones de app',
  AD_RECALL_LIFT: 'Recuerdo de anuncio',
  ENGAGED_USERS: 'Usuarios comprometidos',
  EVENT_RESPONSES: 'Respuestas a evento',
  IMPRESSIONS: 'Impresiones',
  LEAD_GENERATION: 'Generación de leads',
  QUALITY_LEAD: 'Leads de calidad',
  LINK_CLICKS: 'Clics en enlace',
  OFFSITE_CONVERSIONS: 'Conversiones externas',
  PAGE_LIKES: 'Me gusta de página',
  POST_ENGAGEMENT: 'Interacción con publicación',
  QUALITY_CALL: 'Llamadas de calidad',
  REACH: 'Alcance',
  LANDING_PAGE_VIEWS: 'Vistas de landing page',
  VISIT_INSTAGRAM_PROFILE: 'Visitas al perfil de Instagram',
  VALUE: 'Valor',
  THRUPLAY: 'ThruPlay',
  DERIVED_EVENTS: 'Eventos derivados',
  CONVERSATIONS: 'Conversaciones',
  IN_APP_VALUE: 'Valor en app',
  MESSAGING_PURCHASE_CONVERSION: 'Conversión de compra por mensaje',
  MESSAGING_APPOINTMENT_CONVERSION: 'Conversión de cita por mensaje',
  SUBSCRIBERS: 'Suscriptores',
  REMINDERS_SET: 'Recordatorios configurados',
};

const adSetDestinationTypeLabels: Record<string, string> = {
  WHATSAPP: 'WhatsApp',
  MESSENGER: 'Messenger',
  INSTAGRAM_DIRECT: 'Instagram Direct',
  MESSAGING_MESSENGER_WHATSAPP: 'Messenger + WhatsApp',
  MESSAGING_INSTAGRAM_DIRECT_MESSENGER: 'Instagram + Messenger',
  MESSAGING_INSTAGRAM_DIRECT_WHATSAPP: 'Instagram + WhatsApp',
  MESSAGING_INSTAGRAM_DIRECT_MESSENGER_WHATSAPP: 'Instagram + Messenger + WhatsApp',
  WEBSITE: 'Sitio web',
  APP: 'App',
  PHONE_CALL: 'Llamada',
  LEAD_FORM_AND_CALL: 'Formulario y llamada',
  ON_AD: 'En el anuncio',
  ON_POST: 'En la publicación',
  ON_VIDEO: 'En el video',
  ON_PAGE: 'En la página',
  ON_EVENT: 'En el evento',
  SHOP_AUTOMATIC: 'Tienda',
  FACEBOOK: 'Facebook',
};

const WHATSAPP_DESTINATION_TYPES = new Set([
  'WHATSAPP',
  'MESSAGING_MESSENGER_WHATSAPP',
  'MESSAGING_INSTAGRAM_DIRECT_WHATSAPP',
  'MESSAGING_INSTAGRAM_DIRECT_MESSENGER_WHATSAPP',
]);

const humanize = (value: string): string => value.replace(/_/g, ' ').toLowerCase();

export const formatCampaignObjective = (value: string | null | undefined): string | null => {
  if (!value) return null;
  return campaignObjectiveLabels[value] ?? humanize(value);
};

export const formatAdSetOptimizationGoal = (value: string | null | undefined): string | null => {
  if (!value) return null;
  return adSetOptimizationGoalLabels[value] ?? humanize(value);
};

export const formatAdSetDestinationType = (value: string | null | undefined): string | null => {
  if (!value || value === 'UNDEFINED') return null;
  return adSetDestinationTypeLabels[value] ?? humanize(value);
};

export const isWhatsAppDestinationType = (value: string | null | undefined): boolean =>
  Boolean(value) && WHATSAPP_DESTINATION_TYPES.has(value as string);
